import type { FlightCardData } from '@massilia/queries'

import { Checkbox, cn } from '@massilia/ui'
import { useMemo, useState } from 'react'

import { Route } from '@/routes/_booking/flights.results'
import { useFilters } from '@/utils/use-search-params'

import { FilterDrawer } from './filter-drawer'
import {
  formatSegmentLabel,
  getAllSegmentsFromOffers,
  getHourFromISOString,
  getSegmentFromOffer,
} from './filter-utils'
import { FlightCountBadge } from './flight-count-badge'

export interface SegmentTimeFilter {
  arrivalTimeSlots: TimeSlot[]
  departureTimeSlots: TimeSlot[]
  segmentIndex: number
}

export type TimeSlot = '0-6' | '6-12' | '12-18' | '18-24'

interface TimeFilterProps {
  offers: FlightCardData[]
}

const TIME_SLOTS: { label: string; value: TimeSlot }[] = [
  { label: '0h00 - 5h59', value: '0-6' },
  { label: '6h00 - 11h59', value: '6-12' },
  { label: '12h00 - 17h59', value: '12-18' },
  { label: '18h00 - 23h59', value: '18-24' },
]

export function TimeFilter({ offers }: TimeFilterProps) {
  const {
    filters: { filterTimeRanges },
    setFilters,
  } = useFilters(Route.id)
  let value: SegmentTimeFilter[] = []

  if (filterTimeRanges) {
    try {
      value = JSON.parse(filterTimeRanges) as SegmentTimeFilter[]
    } catch {
      value = []
    }
  }

  const segments = useMemo(() => getAllSegmentsFromOffers(offers), [offers])
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0)

  // Calculate flight counts per time slot for the active segment
  const getTimeSlotCounts = (segmentIndex: number) => {
    const counts = {
      arrival: { '0-6': 0, '12-18': 0, '18-24': 0, '6-12': 0 },
      departure: { '0-6': 0, '12-18': 0, '18-24': 0, '6-12': 0 },
    }

    offers.forEach((offer) => {
      const segment = getSegmentFromOffer(offer, segmentIndex)
      if (!segment) return

      // Get departure and arrival hours
      const depHour = getHourFromISOString(segment.departureTime)
      const arrHour = getHourFromISOString(segment.arrivalTime)

      // Count departure slots
      if (depHour >= 0 && depHour < 6) counts.departure['0-6']++
      else if (depHour >= 6 && depHour < 12) counts.departure['6-12']++
      else if (depHour >= 12 && depHour < 18) counts.departure['12-18']++
      else if (depHour >= 18 && depHour <= 24) counts.departure['18-24']++

      // Count arrival slots
      if (arrHour >= 0 && arrHour < 6) counts.arrival['0-6']++
      else if (arrHour >= 6 && arrHour < 12) counts.arrival['6-12']++
      else if (arrHour >= 12 && arrHour < 18) counts.arrival['12-18']++
      else if (arrHour >= 18 && arrHour <= 24) counts.arrival['18-24']++
    })

    return counts
  }

  const handleTimeSlotChange = (
    segmentIndex: number,
    type: 'arrival' | 'departure',
    timeSlot: TimeSlot,
    checked: boolean
  ) => {
    const existingFilterIndex = value.findIndex((f) => f.segmentIndex === segmentIndex)
    const newFilters = [...value]

    if (existingFilterIndex >= 0) {
      const existingFilter = newFilters[existingFilterIndex]
      const currentSlots =
        type === 'departure' ? existingFilter.departureTimeSlots : existingFilter.arrivalTimeSlots
      const updatedSlots = checked
        ? [...currentSlots, timeSlot]
        : currentSlots.filter((slot) => slot !== timeSlot)

      newFilters[existingFilterIndex] = {
        ...existingFilter,
        [type === 'departure' ? 'departureTimeSlots' : 'arrivalTimeSlots']: updatedSlots,
      }
    } else {
      const newFilter: SegmentTimeFilter = {
        arrivalTimeSlots: [],
        departureTimeSlots: [],
        segmentIndex,
        [type === 'departure' ? 'departureTimeSlots' : 'arrivalTimeSlots']: [timeSlot],
      }
      newFilters.push(newFilter)
    }

    setFilters({ filterTimeRanges: newFilters.length > 0 ? JSON.stringify(newFilters) : undefined })
  }

  const getCurrentTimeSlots = (segmentIndex: number, type: 'arrival' | 'departure'): TimeSlot[] => {
    const filter = value.find((f) => f.segmentIndex === segmentIndex)
    return filter
      ? type === 'departure'
        ? filter.departureTimeSlots
        : filter.arrivalTimeSlots
      : []
  }

  if (segments.length === 0) {
    return null
  }

  return (
    <FilterDrawer defaultOpen={false} title="Horaires de vol">
      <div className="space-y-6">
        {/* Segment View Switcher */}
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4">
          {segments.map((segment, index) => (
            <button
              className={cn(
                'rounded-md px-3 py-1 text-sm font-medium whitespace-nowrap transition-colors',
                {
                  'border-b-2 border-blue-500 bg-blue-50 text-blue-700':
                    activeSegmentIndex === index,
                },
                { 'text-gray-600 hover:text-gray-900': activeSegmentIndex !== index }
              )}
              key={index}
              onClick={() => {
                setActiveSegmentIndex(index)
              }}
              type="button"
            >
              {formatSegmentLabel(segment)}
            </button>
          ))}
        </div>

        {/* Time Slot Selection for Active Segment */}
        {/* Departure Time Slots */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Départ</h4>
          <div className="space-y-2">
            {TIME_SLOTS.map((slot) => {
              const count = getTimeSlotCounts(activeSegmentIndex).departure[slot.value]
              const isDisabled = count === 0
              return (
                <div
                  className="flex items-center justify-between space-x-2"
                  key={`dep-${slot.value}`}
                >
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={getCurrentTimeSlots(activeSegmentIndex, 'departure').includes(
                        slot.value
                      )}
                      disabled={isDisabled}
                      onCheckedChange={(checked) => {
                        handleTimeSlotChange(
                          activeSegmentIndex,
                          'departure',
                          slot.value,
                          checked as boolean
                        )
                      }}
                    />
                    <label
                      className={cn(
                        'text-sm',
                        { 'text-gray-400': isDisabled },
                        { 'text-gray-700': !isDisabled }
                      )}
                    >
                      {slot.label}
                    </label>
                  </div>
                  <FlightCountBadge count={count} disabled={isDisabled} />
                </div>
              )
            })}
          </div>
        </div>

        {/* Arrival Time Slots */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Arrivée</h4>
          <div className="space-y-2">
            {TIME_SLOTS.map((slot) => {
              const count = getTimeSlotCounts(activeSegmentIndex).arrival[slot.value]
              const isDisabled = count === 0
              return (
                <div
                  className="flex items-center justify-between space-x-2"
                  key={`arr-${slot.value}`}
                >
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={getCurrentTimeSlots(activeSegmentIndex, 'arrival').includes(
                        slot.value
                      )}
                      disabled={isDisabled}
                      onCheckedChange={(checked) => {
                        handleTimeSlotChange(
                          activeSegmentIndex,
                          'arrival',
                          slot.value,
                          checked as boolean
                        )
                      }}
                    />
                    <label
                      className={cn(
                        'text-sm',
                        { 'text-gray-400': isDisabled },
                        { 'text-gray-700': !isDisabled }
                      )}
                    >
                      {slot.label}
                    </label>
                  </div>
                  <FlightCountBadge count={count} disabled={isDisabled} />
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </FilterDrawer>
  )
}
